/**
 * How a `partially_succeeded` run reads (#817, owner decision 2026-10-12): "Partly failed", then
 * what failed, "Partly failed: 1 of 2 connections failed (HR)". One rule for the Runs list, the
 * run page, the Overview and the run notifications, so no screen says "Succeeded with warning"
 * while Connections says the same connection Failed.
 *
 * A connection reads Failed on Connections when any of its own steps failed (extract or load);
 * this counts the same connections, so the two screens agree on which one failed, except that a
 * connection that landed some of its tables reads "partly loaded" here (#1483). A run is
 * partly failed without a failed connection in two ways (`resolveRunStatus`,
 * `runner/steps/finalize.ts`): its transformations failed after the import succeeded, or an
 * extract reported some tables skipped. Since #443 a run where no connection landed its data is
 * `failed`, so N of M reaches M only when every failed connection's load still landed some of
 * its tables (`connectionOutcome` reads that connection as landed in part, Connections reads it
 * Failed, and this counts it as failed with it). Pure; names only, never an error text (SEC-17).
 */

/** The status word on every badge, filter and count. */
export const PARTLY_FAILED_LABEL = "Partly failed";

export interface PartlyFailedFacts {
  /** The connections the run imported. */
  connections: number;
  /** The display names of the connections with a failed step, in the order to list them. */
  failedConnectionNames: readonly string[];
  /**
   * How many connections failed, when more failed than `failedConnectionNames` names (a list that
   * was cut). Defaults to the names' length.
   */
  failedConnectionCount?: number;
  /**
   * #1483: the display names of connections with a failed step that still landed some of their
   * tables. They read "partly loaded", never "failed", so the sentence does not say nothing landed.
   * Not in `failedConnectionNames`.
   */
  partlyLoadedConnectionNames?: readonly string[];
  /** True when the run-level transform step failed. */
  transformFailed?: boolean;
  /** Tables the run covered, and those that did not load; null when nobody reported them. */
  tables?: number | null;
  tablesNotLoaded?: number | null;
}

function countOf(n: number, one: string, many: string): string {
  return `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;
}

/** What failed, without the status word: "1 of 2 connections failed (HR)". */
export function partlyFailedDetail(facts: PartlyFailedFacts): string {
  const parts: string[] = [];
  const failed = Math.max(facts.failedConnectionCount ?? 0, facts.failedConnectionNames.length);
  if (failed > 0) {
    const total = Math.max(facts.connections, failed);
    const names =
      facts.failedConnectionNames.length > 0 ? ` (${facts.failedConnectionNames.join(", ")})` : "";
    parts.push(
      `${failed.toLocaleString("en-US")} of ${countOf(total, "connection", "connections")} failed${names}`,
    );
  }
  const partly = facts.partlyLoadedConnectionNames ?? [];
  if (partly.length > 0) {
    const total = Math.max(facts.connections, failed + partly.length);
    parts.push(
      `${partly.length.toLocaleString("en-US")} of ${countOf(total, "connection", "connections")} partly loaded (${partly.join(", ")})`,
    );
  }
  if (facts.transformFailed) parts.push("the transformations failed");
  // Sentences, never a semicolon (customer copy): each part after the first starts a new one.
  if (parts.length > 0) {
    return parts
      .map((part, i) => (i === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1)))
      .join(". ");
  }
  const { tables, tablesNotLoaded } = facts;
  if (tables !== null && tables !== undefined && tablesNotLoaded && tablesNotLoaded > 0) {
    return `${tablesNotLoaded.toLocaleString("en-US")} of ${countOf(tables, "table", "tables")} did not load`;
  }
  return "some tables did not load";
}

/** The whole sentence: "Partly failed: 1 of 2 connections failed (HR)". */
export function partlyFailedSentence(facts: PartlyFailedFacts): string {
  return `${PARTLY_FAILED_LABEL}: ${partlyFailedDetail(facts)}`;
}
